import { useEffect, useState } from "react";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { Link, useNavigate } from "react-router-dom";
import {
    IconLoader,
    IconMail,
    IconUsers,
    IconLogout,
    IconUserCheck,
    IconUserPlus,
    IconEdit,
    IconUser,
    IconFileText,
    IconX,
    IconClipboardList,
    IconCheck,
    IconClock,
    IconAlertTriangle,
    IconDownload,
    IconEye,
    IconCurrencyEuro,
    IconCalendar,
    IconSchool,
    IconFile,
    IconFileCheck
} from "@tabler/icons-react";


interface Person {
    id: string;
    firstName: string;
    lastName: string;
    phone: string;
    address: string;
    houseNumber: string;
    city: string;
    psc: string;
    email: string;
    altemail?: string;
    type: 'PARENT' | 'ADULT' | 'KID';
    gender?: 'MALE' | 'FEMALE';
}

interface UserData {
    email: string;
    altemail?: string;
    person: Person[];
}

interface Kruzok {
    id: string;
    name: string;
    description?: string;
    price: number;
    time: string;
    isStretko: boolean;
}

interface AdminSubmition {
    id: string;
    odovzdalVP?: boolean;
    odovzdalCP?: boolean;
    odovzdalRO?: boolean;
}

interface Prihlaska {
    id: string;
    status: 'DRAFT' | 'GENERATED' | 'REJECTED' | 'CANCELLED' | 'ACCEPTED';
    isVP?: boolean;
    isCp?: boolean;
    toPay?: number;
    createdAt: string;
    updatedAt: string;
    hash: string;
    person: Person;
    kruzky: Kruzok[];
    adminsubmition?: AdminSubmition[];
}

const Main = () => {
    const { user, loading: userLoading } = useCurrentUser();
    const navigate = useNavigate();
    const [data, setData] = useState<UserData | null>(null);
    const [prihlaskas, setPrihlaskas] = useState<Prihlaska[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Date validation state
    const [dateStatus, setDateStatus] = useState<{
        canCreate: boolean;
        canEdit: boolean;
        canSubmit: boolean;
        message: string | null;
        startDate: string | null;
        finalDate: string | null;
    } | null>(null);



    const handleLogout = async () => {
        try {
            await fetch(`${process.env.REACT_APP_PATH}/api/user/logout`, {
                method: 'POST',
                credentials: 'include',
            });
            // Clear cookies and redirect to login
            document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            navigate('/user-login');
        } catch (err) {
            console.error('Logout failed:', err);
            // Force redirect anyway
            document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            navigate('/user-login');
        }
    };

    useEffect(() => {
        const fetchUser = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch user profile
                const res = await fetch(`${process.env.REACT_APP_PATH}/api/user/data/profile`, {
                    credentials: 'include',
                });

                if (!res.ok) {
                    console.error('Profile fetch failed:', res.status, res.statusText);
                    const errorText = await res.text();
                    console.error('Error response:', errorText);
                    setError(`Failed to load profile: ${res.status} ${res.statusText}`);
                    setLoading(false);
                    return;
                }
                const data = await res.json();
                setData(data.data);

                // Fetch prihlaskas
                const prihlaskasRes = await fetch(`${process.env.REACT_APP_PATH}/api/prihlaska/`, {
                    credentials: 'include',
                });

                if (prihlaskasRes.ok) {
                    const prihlaskasData = await prihlaskasRes.json();
                    setPrihlaskas(prihlaskasData.data || []);
                }

                // Fetch date validation status
                const dateStatusRes = await fetch(`${process.env.REACT_APP_PATH}/api/prihlaska/date-status`, {
                    credentials: 'include',
                });

                if (dateStatusRes.ok) {
                    const dateStatusData = await dateStatusRes.json();
                    setDateStatus(dateStatusData.data);
                }

                setLoading(false);
            } catch (err) {
                console.error('Failed to fetch user profile:', err);
                setError('Network error while fetching profile');
                setLoading(false);
            }
        };
        if (user) {
            console.log('User is available, fetching profile for:', user.email);
            fetchUser();
        } else {
            setLoading(false);
        }
    }, [user]);

    // Utility functions for prihlaska display
    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'DRAFT':
                return <IconClock className="h-4 w-4" />;
            case 'GENERATED':
                return <IconCheck className="h-4 w-4" />;
            case 'ACCEPTED':
                return <IconCheck className="h-4 w-4" />;
            case 'REJECTED':
                return <IconX className="h-4 w-4" />;
            case 'CANCELLED':
                return <IconAlertTriangle className="h-4 w-4" />;
            default:
                return <IconClock className="h-4 w-4" />;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'DRAFT':
                return 'bg-yellow-100 text-yellow-800';
            case 'GENERATED':
                return 'bg-blue-100 text-blue-800';
            case 'ACCEPTED':
                return 'bg-green-100 text-green-800';
            case 'REJECTED':
                return 'bg-red-100 text-red-800';
            case 'CANCELLED':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'DRAFT':
                return 'Koncept';
            case 'GENERATED':
                return 'Odoslaná';
            case 'ACCEPTED':
                return 'Prijatá';
            case 'REJECTED':
                return 'Zamietnutá';
            case 'CANCELLED':
                return 'Zrušená';
            default:
                return status;
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('sk-SK', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Sort helper: non-stretko first, then stretko; alphabetical within groups
    const sortKruzkyByGroup = (list: Kruzok[]) => {
        return list.slice().sort((a, b) => {
            if (a.isStretko === b.isStretko) return a.name.localeCompare(b.name, 'sk');
            return a.isStretko ? 1 : -1;
        });
    };

    if (!user || loading || userLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
                <header className="bg-white shadow-sm border-b border-gray-200">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-center h-16">
                            <div className="flex items-center">
                                <img src="/img/3h.png" alt="Logo" className="w-10 h-10 mr-3" />
                                <h1 className="text-xl font-bold text-gray-900">SCVČ Tri Hôrky</h1>
                            </div>
                        </div>
                    </div>
                </header>

                <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                    <div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full">
                        <div className="text-center">
                            <IconLoader className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
                            <h2 className="text-xl font-semibold text-gray-900 mb-2">Načítavam vaše údaje...</h2>
                            {error && (
                                <div className="mt-4 p-4 bg-red-50 rounded-md">
                                    <p className="text-red-700 text-sm">{error}</p>
                                    <button
                                        onClick={() => window.location.reload()}
                                        className="mt-2 px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
                                    >
                                        Skúsiť znovu
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
                <header className="bg-white shadow-sm border-b border-gray-200">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-center h-16">
                            <div className="flex items-center">
                                <img src="/img/3h.png" alt="Logo" className="w-10 h-10 mr-3" />
                                <h1 className="text-xl font-bold text-gray-900">SCVČ Tri Hôrky</h1>
                            </div>
                        </div>
                    </div>
                </header>

                <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                    <div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full">
                        <div className="text-center">
                            <IconLoader className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
                            <h2 className="text-xl font-semibold text-gray-900">Načítavam údaje...</h2>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (data.person.length === 0) {
        // Redirect new users to type selection page
        navigate('/user-type-selection');
        return null;
    }

    // Main registered person (adult or parent) for quick overview in the sidebar
    const mainPerson = data.person.find(p => p.type === 'PARENT' || p.type === 'ADULT') || null;

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
            {/* Enhanced Header */}
            <header className="bg-white shadow-lg border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
                    <div className="flex justify-between items-center h-14 sm:h-16">
                        <div className="flex items-center min-w-0 flex-1">
                            <img src="/img/3h.png" alt="Logo" className="w-8 h-8 sm:w-10 sm:h-10 mr-2 sm:mr-3 flex-shrink-0" />
                            <div className="min-w-0">
                                <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">SCVČ Tri Hôrky</h1>
                                <p className="text-xs text-gray-500 hidden sm:block">Váš účet</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
                            <div className="hidden md:flex items-center space-x-2 text-sm text-gray-600 bg-gray-100 rounded-full px-3 py-1">
                                <IconMail className="h-4 w-4" />
                                <span className="">{data.email}</span>
                            </div>
                            <button
                                onClick={handleLogout}
                                className="flex items-center space-x-1 text-xs sm:text-sm text-red-600 hover:text-red-800 font-medium bg-red-50 hover:bg-red-100 rounded-full px-2 sm:px-3 py-1 transition-colors"
                            >
                                <IconLogout className="h-3 w-3 sm:h-4 sm:w-4" />
                                <span className="hidden sm:inline">Odhlásiť sa</span>
                                <span className="sm:hidden">Odhlás.</span>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
                {/* Date restriction notice */}
                {dateStatus && !dateStatus.canCreate && dateStatus.message && (
                    <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-md">
                        <div className="flex items-center">
                            <IconX className="h-5 w-5 text-red-400 mr-2" />
                            <p className="text-red-700 font-medium">{dateStatus.message}</p>
                        </div>
                    </div>
                )}

                {/* Dashboard Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
                        <div className="flex items-center">
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <IconUsers className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="ml-4">
                                <h3 className="text-lg font-semibold text-gray-900">{data.person.filter(e => e.type !== 'PARENT').length}</h3>
                                <p className="text-sm text-gray-600">Registrované osoby</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
                        <div className="flex items-center">
                            <div className="p-3 bg-green-100 rounded-lg">
                                <IconClipboardList className="h-6 w-6 text-green-600" />
                            </div>
                            <div className="ml-4">
                                <h3 className="text-lg font-semibold text-gray-900">{prihlaskas.length}</h3>
                                <p className="text-sm text-gray-600">Prihlášky</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
                        <div className="flex items-center">
                            <div className="p-3 bg-purple-100 rounded-lg">
                                <IconSchool className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className="ml-4">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {prihlaskas.reduce((total, p) => total + p.kruzky.length, 0)}
                                </h3>
                                <p className="text-sm text-gray-600">Krúžky spolu</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Prihlaskas Overview */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden mb-6">
                            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <IconClipboardList className="h-5 w-5 text-blue-600 mr-2" />
                                        <h3 className="text-lg font-semibold text-gray-900">Prehľad prihlášok</h3>
                                    </div>
                                </div>
                                <p className="text-sm text-gray-600 mt-1">Všetky vaše prihlášky na krúžky</p>
                            </div>

                            <div className="p-6">
                                {prihlaskas.length === 0 ? (
                                    <div className="text-center py-8">
                                        <IconClipboardList className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">Žiadne prihlášky</h3>
                                        <p className="text-gray-600 mb-4">Zatiaľ nemáte vytvorené žiadne prihlášky na krúžky.</p>
                                        {(data.person.some(p => p.type !== 'PARENT') && dateStatus && dateStatus?.canCreate) && (
                                            <div className="flex flex-wrap justify-center gap-2">
                                                {data.person.filter(p => p.type !== 'PARENT').map(person => (
                                                    <Link
                                                        key={person.id}
                                                        to={`/create-prihlaska/${person.id}`}
                                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                                                    >
                                                        <IconFileText className="h-4 w-4 mr-2" />
                                                        Vytvoriť prihlášku pre {person.firstName}
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {prihlaskas.map((prihlaska) => (
                                            <div key={prihlaska.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center">
                                                        <div className="flex items-center space-x-2">
                                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(prihlaska.status)}`}>
                                                                {getStatusIcon(prihlaska.status)}
                                                                <span className="ml-1">{getStatusText(prihlaska.status)}</span>
                                                            </span>
                                                        </div>
                                                        <div className="ml-3">
                                                            <h4 className="text-lg font-medium text-gray-900">
                                                                {prihlaska.person.firstName} {prihlaska.person.lastName}
                                                            </h4>
                                                            <p className="text-sm text-gray-600">
                                                                <IconCalendar className="h-4 w-4 inline mr-1" />
                                                                Vytvorená {formatDate(prihlaska.createdAt)}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center space-x-2">
                                                        {prihlaska.toPay && (
                                                            <span className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
                                                                <IconCurrencyEuro className="h-4 w-4 mr-1" />
                                                                {prihlaska.toPay.toFixed(2)}€
                                                            </span>
                                                        )}

                                                        <Link
                                                            to={`/create-prihlaska/${prihlaska.person.id}`}
                                                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200 transition-colors"
                                                        >
                                                            <IconEye className="h-4 w-4 mr-1" />
                                                            Zobraziť
                                                        </Link>

                                                        {prihlaska.status === 'GENERATED' && (
                                                            <a
                                                                href={`${process.env.REACT_APP_PATH}/api/prihlaska/${prihlaska.id}/download`}
                                                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-green-600 bg-green-100 hover:bg-green-200 transition-colors"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <IconDownload className="h-4 w-4 mr-1" />
                                                                PDF
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>

                                                {prihlaska.kruzky.length > 0 && (
                                                    <div className="mt-3">
                                                        <h5 className="text-sm font-medium text-gray-700 mb-2">Krúžky:</h5>
                                                        <div className="flex flex-wrap gap-2">
                                                            {sortKruzkyByGroup(prihlaska.kruzky).map((kruzok) => (
                                                                <span
                                                                    key={kruzok.id}
                                                                    className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                                                                >
                                                                    <IconSchool className="h-3 w-3 mr-1" />
                                                                    {kruzok.name}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Admin submission status */}
                                                {prihlaska.status !== 'DRAFT' && prihlaska.adminsubmition && prihlaska.adminsubmition.length > 0 && (
                                                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                                        <h5 className="text-sm font-medium text-gray-700 mb-2">Stav spracovania:</h5>
                                                        <div className="space-y-2">
                                                            {prihlaska.adminsubmition.map((admin) => (
                                                                <div key={admin.id} className="flex flex-wrap gap-3 text-sm">


                                                                    {/* VP document status */}
                                                                    {prihlaska.isVP && (
                                                                        <div className="flex items-center">
                                                                            <IconFileCheck className="h-4 w-4 mr-1 text-blue-600" />
                                                                            <span className="text-gray-600">Vzdelávací poukaz:</span>
                                                                            <span className={`ml-1 font-medium ${admin.odovzdalVP ? 'text-green-600' : 'text-red-500'}`}>
                                                                                {admin.odovzdalVP ? 'Odovzdaný' : 'Neodovzdaný'}
                                                                            </span>
                                                                        </div>
                                                                    )}

                                                                    {/* CP document status */}
                                                                    {prihlaska.isCp && (
                                                                        <div className="flex items-center">
                                                                            <IconFile className="h-4 w-4 mr-1 text-purple-600" />
                                                                            <span className="text-gray-600">Čestné prehlásenie:</span>
                                                                            <span className={`ml-1 font-medium ${admin.odovzdalCP ? 'text-green-600' : 'text-red-500'}`}>
                                                                                {admin.odovzdalCP ? 'Odovzdané' : 'Neodovzdané'}
                                                                            </span>
                                                                        </div>
                                                                    )}

                                                                    {/* RO document status for kids */}
                                                                    {prihlaska.person.type === 'KID' && (
                                                                        <div className="flex items-center">
                                                                            <IconFile className="h-4 w-4 mr-1 text-orange-600" />
                                                                            <span className="text-gray-600">Rodičovský súhlas:</span>
                                                                            <span className={`ml-1 font-medium ${admin.odovzdalRO ? 'text-green-600' : 'text-red-500'}`}>
                                                                                {admin.odovzdalRO ? 'Odovzdaný' : 'Neodovzdaný'}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Registered Persons */}
                        {user.person?.find(e => e.type === 'PARENT') && <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <IconUsers className="h-5 w-5 text-blue-600 mr-2" />
                                        <h3 className="text-lg font-semibold text-gray-900">Registrované osoby</h3>
                                    </div>
                                    {(data.person.some(person => person.type === 'PARENT') && !data.person.some(person => person.type === 'ADULT')) && (
                                        <Link
                                            to="/create?type=KID"
                                            className="inline-flex items-center px-3 py-1 mt-2 border border-transparent  font-medium rounded-md text-red-600 bg-red-100 hover:bg-red-200 transition-colors duration-200"
                                        >
                                            <IconUserPlus className="h-5 w-5 mr-1" />
                                            Pridať dieťa
                                        </Link>
                                    )}
                                </div>
                                <p className="text-sm text-gray-600">Zoznam všetkých registrovaných osôb</p>
                            </div>

                            <div className="p-6">
                                <div className="space-y-3">
                                    {data.person.filter(person => person.type === 'KID').map((person) => (
                                        <div key={person.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                            <div className="flex items-center flex-1">
                                                <div className={`p-2 rounded-lg bg-green-100`}>
                                                    <IconUser className={`h-5 w-5 text-green-600`} />
                                                </div>
                                                <div className="ml-3 flex-1">
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <p className="font-medium text-gray-900">{person.firstName} {person.lastName}</p>
                                                            <div className="flex items-center text-sm text-gray-500">
                                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800`}>
                                                                    Dieťa
                                                                </span>

                                                            </div>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            {
                                                                dateStatus && !dateStatus.canCreate ? (
                                                                    <span className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-400 bg-gray-100 cursor-not-allowed opacity-50">
                                                                        <IconFileText className="h-4 w-4 mr-1" />
                                                                        Prihláška
                                                                    </span>
                                                                ) : (
                                                                    <Link
                                                                        to={`/create-prihlaska/${person.id}`}
                                                                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-green-600 bg-green-100 hover:bg-green-200 transition-colors duration-200"
                                                                    >
                                                                        <IconFileText className="h-4 w-4 mr-1" />
                                                                        Prihláška
                                                                    </Link>
                                                                )
                                                            }
                                                            <Link
                                                                to={`/edit-person/${person.id}`}
                                                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200 transition-colors duration-200"
                                                            >
                                                                <IconEdit className="h-4 w-4 mr-1" />
                                                                Upraviť
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>}
                    </div>

                    {/* Account Information Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <IconUserCheck className="h-5 w-5 text-blue-600 mr-2" />
                                        <h3 className="text-lg font-semibold text-gray-900">Informácie o účte</h3>
                                    </div>
                                    <Link
                                        to={`/edit-person/${user?.person?.find(e => e.type === 'PARENT' || e.type === 'ADULT')?.id || ''}`}
                                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200 transition-colors duration-200"
                                    >
                                        <IconEdit className="h-4 w-4 mr-1" />
                                        Upraviť
                                    </Link>
                                </div>
                                <p className="text-sm text-gray-600 mt-1">Vaše kontaktné údaje</p>
                            </div>

                            <div className="p-6">
                                <div className="space-y-4">

                                    {mainPerson && (
                                        <div className=" bg-white flex items-start">
                                            <div className="p-2 bg-indigo-50 rounded-md">
                                                <IconUser className="h-5 w-5 text-indigo-600" />
                                            </div>
                                            <div className="ml-3 flex-1">

                                                <p className="font-medium text-gray-900">{mainPerson.firstName} {mainPerson.lastName}</p>
                                                <p className="text-sm text-gray-600 pb-4">
                                                    {
                                                        mainPerson.type !== 'PARENT' ? (
                                                            <>{mainPerson.gender === 'MALE' ? 'Muž' : 'Žena'}</>
                                                        ) : (
                                                            <>{mainPerson.gender === 'MALE' ? 'Otec / Zákonný zástupca' : 'Matka / Zákonný zástupca'}</>
                                                        )
                                                    }

                                                </p>
                                                <p className="text-sm text-gray-600">
                                                    {mainPerson.address || ''}{mainPerson.houseNumber ? ` ${mainPerson.houseNumber}` : ''}
                                                </p>
                                                <p className="text-sm text-gray-600">
                                                    {mainPerson.psc + "," || ''}{mainPerson.city ? ` ${mainPerson.city}` : ''}
                                                </p>
                                                <p className="text-sm text-gray-600">
                                                    {mainPerson.phone || ''}
                                                </p>
                                                <p className="text-sm text-gray-600">
                                                    {mainPerson.email || ''}
                                                </p>


                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Main;
